import { Component, computed, effect, input, InputSignal, signal, Signal, WritableSignal, inject } from '@angular/core';
import { AbstractComment, ActivityDisplayModule } from '@lib/comment';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
    BottomSheetService,
    FileDownloadModule,
    ScannedImageComponent,
    SortDirection,
    UploadBottomSheetResult,
} from '@lib/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogOptions } from '@lib/util';
import { TranslateModule } from '@ngx-translate/core';
import { IInvoiceHistory } from '../detail/types/IStandaloneInvoiceDetail';
import { MatIconButton } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { InvoiceCommentEntry } from './types/InvoiceHistoryEntry';
import { UserService } from '@lib/user';
import { IimCardModule, IimSpinnerModule } from '@iim-angular/components';
import { InvoiceDetail } from '../detail/types/InvoiceDetail';
import { InvoiceDataService } from '../invoice-data.service';
import { finalize } from 'rxjs/operators';

class InvoiceHistoryDialogData {
    constructor(
        public readonly invoiceId: string,
        public readonly history: IInvoiceHistory,
    ) {}
}

@Component({
    selector: 'invoice-activity',
    imports: [
        TranslateModule,
        FileDownloadModule,
        ReactiveFormsModule,
        FormsModule,
        ActivityDisplayModule,
        MatIconButton,
        CommonModule,
        IimCardModule,
        ScannedImageComponent,
        IimSpinnerModule,
    ],
    templateUrl: './invoice-activity.component.html',
    styleUrl: './invoice-activity.component.scss',
})
export class InvoiceActivityComponent {
    private readonly dialogRef = inject<MatDialogRef<InvoiceActivityComponent>>(MatDialogRef, { optional: true })!;
    private readonly data = inject<InvoiceHistoryDialogData>(MAT_DIALOG_DATA, { optional: true })!;
    private readonly dialog = inject(MatDialog);
    private readonly userService = inject(UserService);
    private readonly bottomSheetService = inject(BottomSheetService);
    private readonly dataService = inject(InvoiceDataService);

    invoice: InputSignal<InvoiceDetail> = input.required();

    files: Signal<FormDataEntryValue[]> = computed(() => {
        const formData: FormData | undefined = this.formData();
        if (formData) {
            return formData.getAll('files');
        }

        return [];
    });
    hasFiles: Signal<boolean> = computed(() => {
        const files = this.files();

        return files && files.length > 0;
    });
    images: Signal<FormDataEntryValue[]> = computed(() => {
        const formData: FormData | undefined = this.formData();
        if (formData) {
            return formData.getAll('images');
        }

        return [];
    });
    hasImages: Signal<boolean> = computed(() => {
        const images = this.images();

        return images && images.length > 0;
    });

    private readonly formData: WritableSignal<FormData | undefined> = signal(undefined);
    saving: WritableSignal<boolean> = signal(false);

    readonly commentControl = new FormControl('');

    readonly invoiceId: Signal<string> = computed(() => {
        if (this.isDialog) {
            return this.data.invoiceId;
        }

        return this.invoice().id;
    });

    readonly history: Signal<IInvoiceHistory> = computed(() => {
        if (this.isDialog) {
            return this.data.history;
        }

        return this.invoice().history();
    });

    public allEntries: WritableSignal<AbstractComment[]> = signal([]);

    protected readonly SortDirection = SortDirection;

    get isDialog(): boolean {
        return !!this.dialogRef;
    }

    constructor() {
        effect(() => {
            const history = this.history();
            if (history) {
                this.setComments(history);
            }
        });
    }

    public closeDialog() {
        if (this.dialogRef) {
            this.dialogRef.close(this.allEntries());
        }
    }

    public expandToDialog() {
        if (!this.isDialog) {
            const dialogData = new InvoiceHistoryDialogData(this.invoiceId(), this.history());
            this.dialog
                .open(InvoiceActivityComponent, DialogOptions.LARGE(dialogData))
                .afterClosed()
                .subscribe((updatedEntries: AbstractComment[]) => {
                    if (updatedEntries) {
                        this.allEntries.set(updatedEntries);
                    }
                });
        }
    }

    attachFile() {
        this.bottomSheetService
            .upload({
                multiple: true,
                uploadInternalPossible: false,
            })
            .afterDismissed()
            .subscribe((result: UploadBottomSheetResult) => {
                const formData = result.formData;
                if (result.formData) {
                    for (const entry of this.files()) {
                        formData.append('files', entry);
                    }

                    this.formData.set(formData);
                }
            });
    }

    saveComment() {
        if (this.commentControl.value.trim() || this.hasFiles() || this.hasImages) {
            this.saving.set(true);
            this.dataService
                .addComment(this.invoiceId(), this.commentControl.value, false, this.files(), this.images())
                .pipe(
                    finalize(() => {
                        this.saving.set(false);
                    }),
                )
                .subscribe((updatedHistory: IInvoiceHistory) => {
                    this.commentControl.setValue('');
                    this.formData.set(new FormData());
                    if (this.isDialog) {
                        this.setComments(updatedHistory);
                    } else {
                        this.invoice().history.set(updatedHistory);
                    }
                });
        }
    }

    removeFile(file: FormDataEntryValue) {
        const currentFormData = this.formData();
        if (!currentFormData) {
            return;
        }

        // Create a new FormData object
        const newFormData = new FormData();

        // Copy all files except the one to be removed
        const currentFiles = currentFormData.getAll('files');
        for (const currentFile of currentFiles) {
            if (currentFile !== file) {
                newFormData.append('files', currentFile);
            }
        }

        // Copy all images
        const currentImages = currentFormData.getAll('images');
        for (const image of currentImages) {
            newFormData.append('images', image);
        }

        // Update the formData signal
        this.formData.set(newFormData);
    }

    private async setComments(history: IInvoiceHistory): Promise<void> {
        const currentUser = await this.userService.getMyself();
        this.allEntries.set(history.comments.map((c) => new InvoiceCommentEntry(c, currentUser.id)));
    }

    removeImage(image: FormDataEntryValue) {
        const currentFormData = this.formData();
        if (!currentFormData) {
            return;
        }

        // Create a new FormData object
        const newFormData = new FormData();

        // Copy all files
        const currentFiles = this.files();
        for (const file of currentFiles) {
            newFormData.append('files', file);
        }

        // Copy all images except the one to be removed
        const currentImages = this.images();
        for (const currentImage of currentImages) {
            if (currentImage !== image) {
                newFormData.append('images', currentImage);
            }
        }

        // Update the formData signal
        this.formData.set(newFormData);
    }

    deleteComment(commentId: string) {
        this.dataService.deleteComment(this.invoiceId(), commentId).subscribe((updatedHistory: IInvoiceHistory) => {
            if (this.isDialog) {
                this.setComments(updatedHistory);
            } else {
                this.invoice().history.set(updatedHistory);
            }
        });
    }
}
